import { inject, Injectable } from '@angular/core';
import { isAgency } from '@rma/generic/domain/helpers';
import { CurrentUserContext } from '@rma/generic/ng/user';
import { cache } from 'decorator-cache-getter';
import { combineLatest, map, Observable } from 'rxjs';
import { DashboardCurrentProfileUserContext } from '../../entities/dashboard-profile-user-context.models';
import { DashboardProfileContextService } from './dashboard-profile-context.service';
import { DashboardUserContextService } from './dashboard-user-context.service';

/**
 * DashboardProfileUserContextService
 *
 * Provides a unified, *non-nullable* observable context for the current dashboard user and their selected profile
 *
 * @deprecated Please use `DashboardProfileContextService` Or `DashboardUserContextService` instead.
 * @remarks
 * - Intended for use in dashboard orchestrators and UI components that need to react to changes in user/profile context.
 * - All observables are derived from the underlying `ProfileUserContextService` and `CurrentUserService`.
 */
@Injectable({
  providedIn: 'root',
})
export class DashboardProfileUserContextService {
  private readonly profileContextService = inject(DashboardProfileContextService);
  private readonly userContextService = inject(DashboardUserContextService);

  @cache
  public get profileOrUserContextChanged$(): Observable<string> {
    return this.profileUserContext$.pipe(map(() => new Date().toString()));
  }

  @cache
  public get profileUserContext$(): Observable<DashboardCurrentProfileUserContext> {
    return combineLatest([this.profileContextService.profileContext$, this.userContextService.userContext$]).pipe(
      map(([profile, user]) => ({ profile, user })),
    );
  }

  @cache
  public get isAdminAsAgency$(): Observable<boolean> {
    return combineLatest([this.profileContextService.profileType$, this.userContext$.pipe(map((user) => user.userType))]).pipe(
      map(([profileType, userType]) => isAgency(profileType) && ['AgencyAdmin', 'AgencyAdminAgent'].includes(userType)),
    );
  }

  @cache
  private get userContext$(): Observable<CurrentUserContext> {
    return this.profileUserContext$.pipe(map((x) => x.user));
  }
}
